import { useEffect, useId, useMemo, useRef, useState, type FC, type ReactNode } from 'react'
import classNames from 'classnames'
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder'
import { attachClosestEdge, extractClosestEdge, type Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index'
import { SelectDropdown, type SelectOption } from '../SelectDropdown/SelectDropdown'
import { Button } from '../Button/Button'
import { Tooltip } from '../Tooltip/Tooltip'
import styles from './OrderableMultiSelectDropdown.module.scss'

type ItemControl = 'up' | 'down' | 'remove'

type FocusTarget = { id: string; control: ItemControl } | 'add'

export interface OrderableMultiSelectDropdownProps {
  options: SelectOption[]
  value: string[]
  onChange: (ids: string[]) => void
  ariaLabel: string
  placeholder?: string
  loading?: boolean
  optionsLoaded?: boolean
  disabled?: boolean
  renderItemActions?: (id: string, index: number, label: string) => ReactNode
  removeItemLabel?: string
  maxItems?: number
  maxItemsPlaceholder?: string
  unknownItemMessage?: string
}

interface OrderableItemProps {
  id: string
  index: number
  count: number
  label: string
  dndType: string
  disabled: boolean
  unknownMessage: string | null
  actions?: ReactNode
  removeItemLabel: string
  registerControl: (id: string, control: ItemControl, element: HTMLButtonElement | null) => void
  onMove: (from: number, to: number, control: ItemControl) => void
  onRemove: (index: number) => void
}

const OrderableItem: FC<OrderableItemProps> = ({
  id,
  index,
  count,
  label,
  dndType,
  disabled,
  unknownMessage,
  actions,
  removeItemLabel,
  registerControl,
  onMove,
  onRemove,
}) => {
  const itemRef = useRef<HTMLLIElement>(null)
  const handleRef = useRef<HTMLSpanElement>(null)
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [closestEdge, setClosestEdge] = useState<Edge | null>(null)

  useEffect(() => {
    const element = itemRef.current
    const handle = handleRef.current
    if (!element || !handle || disabled) {
      return
    }
    return combine(
      draggable({
        element,
        dragHandle: handle,
        getInitialData: () => ({ type: dndType, id, index }),
        onDragStart: () => setIsDragging(true),
        onDrop: () => setIsDragging(false),
      }),
      dropTargetForElements({
        element,
        getData: ({ input, element: target }) => attachClosestEdge({ type: dndType, id, index }, { input, element: target, allowedEdges: ['top', 'bottom'] }),
        canDrop: ({ source }) => source.data.type === dndType && source.data.id !== id,
        onDragEnter: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDrag: ({ self }) => setClosestEdge(extractClosestEdge(self.data)),
        onDragLeave: () => setClosestEdge(null),
        onDrop: () => setClosestEdge(null),
      }),
    )
  }, [dndType, id, index, disabled])

  return (
    <li ref={itemRef} className={classNames('position-relative', { 'opacity-50': isDragging })}>
      {closestEdge === 'top' ? <div className={classNames('position-absolute start-0 end-0', styles.dropIndicator, styles.dropIndicatorTop)} /> : null}
      <div
        className={classNames('d-flex align-items-center gap-2 rounded-2 px-3 py-2', {
          'zocks-bg-color-white zocks-border-gray-200': !unknownMessage,
          'zocks-bg-color-warning zocks-border-color-warning-dark': unknownMessage,
        })}
      >
        <span ref={handleRef} className={classNames('d-flex align-items-center', { [styles.dragHandle]: !disabled })} aria-hidden={true}>
          <i className='bi bi-grip-vertical zocks-text-gray-400' />
        </span>
        <span className={classNames('paragraph-small zocks-text-gray-500 text-center', styles.index)}>{index + 1}</span>
        <span className='paragraph-medium flex-grow-1 text-truncate zocks-text-gray-900'>{label}</span>
        {unknownMessage ? (
          <Tooltip title={unknownMessage} placement='top'>
            <i className='bi bi-exclamation-triangle zocks-text-warning-dark' aria-hidden={true} />
            <span className='visually-hidden'>{unknownMessage}</span>
          </Tooltip>
        ) : null}
        {actions}
        <Button
          ref={(element) => registerControl(id, 'up', element)}
          type='button'
          variant='grayGhost'
          className={classNames('d-inline-flex align-items-center justify-content-center flex-shrink-0 p-0', styles.iconButton)}
          onClick={() => onMove(index, index - 1, 'up')}
          disabled={disabled || index === 0}
          aria-label={`Move ${label} up`}
        >
          <i className='bi bi-chevron-up' />
        </Button>
        <Button
          ref={(element) => registerControl(id, 'down', element)}
          type='button'
          variant='grayGhost'
          className={classNames('d-inline-flex align-items-center justify-content-center flex-shrink-0 p-0', styles.iconButton)}
          onClick={() => onMove(index, index + 1, 'down')}
          disabled={disabled || index === count - 1}
          aria-label={`Move ${label} down`}
        >
          <i className='bi bi-chevron-down' />
        </Button>
        <Button
          ref={(element) => registerControl(id, 'remove', element)}
          type='button'
          variant='primaryGhost'
          className={classNames('d-inline-flex align-items-center justify-content-center flex-shrink-0 p-0', styles.iconButton)}
          onClick={() => onRemove(index)}
          disabled={disabled}
          aria-label={`${removeItemLabel} ${label}`}
        >
          <i className='bi bi-trash' />
        </Button>
      </div>
      {closestEdge === 'bottom' ? <div className={classNames('position-absolute start-0 end-0', styles.dropIndicator, styles.dropIndicatorBottom)} /> : null}
    </li>
  )
}

export const OrderableMultiSelectDropdown: FC<OrderableMultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  ariaLabel,
  placeholder,
  loading = false,
  optionsLoaded = true,
  disabled = false,
  renderItemActions,
  removeItemLabel = 'Remove',
  maxItems,
  maxItemsPlaceholder,
  unknownItemMessage = 'Not available',
}) => {
  const dndType = useId()
  const toggleId = `${dndType}-add`
  const controls = useRef(new Map<string, Partial<Record<ItemControl, HTMLButtonElement | null>>>())
  const pendingFocus = useRef<FocusTarget | null>(null)
  const [announcement, setAnnouncement] = useState<string>('')
  const isFull = maxItems !== undefined && value.length >= maxItems
  const availableOptions = useMemo(() => options.filter((option) => !value.includes(option.id)), [options, value])
  const optionLabels = useMemo(() => new Map(options.map((option) => [option.id, option.label])), [options])
  const labelOf = (id: string) => optionLabels.get(id) ?? id

  useEffect(() => {
    const target = pendingFocus.current
    if (!target) {
      return
    }
    pendingFocus.current = null
    if (target === 'add') {
      document.getElementById(toggleId)?.focus()
    } else {
      controls.current.get(target.id)?.[target.control]?.focus()
    }
  }, [value, toggleId])

  const registerControl = (id: string, control: ItemControl, element: HTMLButtonElement | null) => {
    controls.current.set(id, { ...controls.current.get(id), [control]: element })
  }

  const move = (from: number, to: number, control: ItemControl) => {
    if (to < 0 || to >= value.length || from === to) {
      return
    }
    const id = value[from]
    const reachedEdge = (control === 'up' && to === 0) || (control === 'down' && to === value.length - 1)
    pendingFocus.current = { id, control: reachedEdge ? (control === 'up' ? 'down' : 'up') : control }
    setAnnouncement(`${labelOf(id)} moved to position ${to + 1} of ${value.length}`)
    onChange(reorder({ list: value, startIndex: from, finishIndex: to }))
  }

  const remove = (index: number) => {
    const id = value[index]
    const next = value.filter((_, itemIndex) => itemIndex !== index)
    const neighbour = next[index] ?? next[index - 1]
    pendingFocus.current = neighbour ? { id: neighbour, control: 'remove' } : 'add'
    setAnnouncement(`${labelOf(id)} removed`)
    onChange(next)
  }

  const add = (option: SelectOption) => {
    if (isFull) {
      return
    }
    setAnnouncement(`${option.label} added at position ${value.length + 1}`)
    onChange([...value, option.id])
  }

  useEffect(
    () =>
      monitorForElements({
        canMonitor: ({ source }) => source.data.type === dndType,
        onDrop: ({ source, location }) => {
          const target = location.current.dropTargets[0]
          if (!target || target.data.type !== dndType) {
            return
          }
          const startIndex = source.data.index as number
          const finishIndex = getReorderDestinationIndex({
            startIndex,
            indexOfTarget: target.data.index as number,
            closestEdgeOfTarget: extractClosestEdge(target.data),
            axis: 'vertical',
          })
          if (startIndex === finishIndex) {
            return
          }
          const id = value[startIndex]
          setAnnouncement(`${optionLabels.get(id) ?? id} moved to position ${finishIndex + 1} of ${value.length}`)
          onChange(reorder({ list: value, startIndex, finishIndex }))
        },
      }),
    [dndType, value, onChange, optionLabels],
  )

  return (
    <div className='d-flex flex-column gap-2'>
      <SelectDropdown
        options={availableOptions}
        initialSelectedOption={null}
        onOptionSelected={add}
        placeholder={isFull ? (maxItemsPlaceholder ?? `Maximum of ${maxItems} selected`) : placeholder}
        searchable
        clearDropdownOnOptionSelected
        loading={loading}
        disabled={disabled || isFull}
        ariaLabel={ariaLabel}
        toggleId={toggleId}
      />
      {value.length > 0 ? (
        <ul className='list-unstyled d-flex flex-column gap-2 mb-0'>
          {value.map((id, index) => {
            const label = labelOf(id)
            return (
              <OrderableItem
                key={id}
                id={id}
                index={index}
                count={value.length}
                label={label}
                dndType={dndType}
                disabled={disabled}
                unknownMessage={optionsLoaded && !loading && !optionLabels.has(id) ? unknownItemMessage : null}
                actions={renderItemActions?.(id, index, label)}
                removeItemLabel={removeItemLabel}
                registerControl={registerControl}
                onMove={move}
                onRemove={remove}
              />
            )
          })}
        </ul>
      ) : null}
      <div className='visually-hidden' role='status' aria-live='polite'>
        {announcement}
      </div>
    </div>
  )
}
